import type {
	ContentTypeCommentAgentReplyTarget,
	ContentTypeCommentAgentReplyResolution,
	ContentTypeCommentAgentReplySnapshot,
} from '@confluence/content-types-bridge/entry-points/content-types-shell-types.ts';

export type CommentAgentReplyRequest = ContentTypeCommentAgentReplyTarget & { id: string };

type Listener = (snapshot: ContentTypeCommentAgentReplySnapshot) => void;
type Resolve = (resolution: 'confirmed' | 'cancelled') => Promise<void>;
type Entry = {
	request: CommentAgentReplyRequest;
	listeners: Set<Listener>;
	snapshot: ContentTypeCommentAgentReplySnapshot;
	resolve?: Resolve;
	pending?: { input: ContentTypeCommentAgentReplyResolution; promise: Promise<void> };
};
const keyFor = ({ commentId, conversationId }: ContentTypeCommentAgentReplyTarget) =>
	JSON.stringify([commentId, conversationId]);

/** Retains private detail only while an embedded consumer is listening. Execution stays in Chat. */
export class ContentCommentAgentReplyService {
	private entries = new Map<string, Entry>();
	private requestListeners = new Set<() => void>();
	private requests: readonly CommentAgentReplyRequest[] = [];
	private disposed = false;

	// React can replay scope effects after cleanup; old request identities remain invalid.
	activate = (): void => {
		this.disposed = false;
	};
	isDisposed = (): boolean => this.disposed;
	getRequests = (): readonly CommentAgentReplyRequest[] => this.requests;
	subscribeToRequests = (listener: () => void): (() => void) => {
		this.requestListeners.add(listener);
		return () => {
			this.requestListeners.delete(listener);
		};
	};

	observe = (input: ContentTypeCommentAgentReplyTarget, listener: Listener): (() => void) => {
		if (this.disposed) {
			listener({ status: 'unavailable' });
			return () => {};
		}
		const key = keyFor(input);
		const entry: Entry = this.entries.get(key) ?? {
			request: {
				commentId: input.commentId,
				conversationId: input.conversationId,
				id: crypto.randomUUID(),
			},
			listeners: new Set(),
			snapshot: { status: 'loading' },
		};
		this.entries.set(key, entry);
		entry.listeners.add(listener);
		this.notify(entry, listener);
		this.updateRequests();
		return () => {
			entry.listeners.delete(listener);
			if (!entry.listeners.size && this.entries.get(key) === entry) {
				this.entries.delete(key);
				this.updateRequests();
			}
		};
	};

	publish = (
		request: ContentTypeCommentAgentReplyTarget,
		snapshot: ContentTypeCommentAgentReplySnapshot,
		resolve?: Resolve,
	): void => {
		const entry = this.entries.get(keyFor(request));
		// An old controller must not publish into a reopened request for the same comment.
		if (this.disposed || entry?.request !== request) return;
		const changed = entry.snapshot !== snapshot;
		entry.snapshot = snapshot;
		entry.resolve = resolve;
		if (changed) entry.listeners.forEach((listener) => this.notify(entry, listener));
	};

	resolve = async (input: ContentTypeCommentAgentReplyResolution): Promise<void> => {
		if (input.resolution !== 'confirmed' && input.resolution !== 'cancelled')
			throw new Error('Invalid private reply resolution');
		const entry = this.entries.get(keyFor(input));
		if (this.disposed || !entry) throw new Error('Private reply unavailable');
		if (entry.pending) {
			if (
				entry.pending.input.actionToken !== input.actionToken ||
				entry.pending.input.resolution !== input.resolution
			)
				throw new Error('Private reply changed');
			return entry.pending.promise;
		}
		if (
			entry.snapshot.status !== 'ready' ||
			entry.snapshot.actionToken !== input.actionToken ||
			!entry.resolve
		)
			throw new Error('Private reply changed');
		const pending = { input, promise: entry.resolve(input.resolution) };
		entry.pending = pending;
		try {
			await pending.promise;
		} finally {
			if (entry.pending === pending) entry.pending = undefined;
		}
	};

	dispose = (): void => {
		this.disposed = true;
		this.entries.forEach((entry) => {
			entry.snapshot = { status: 'unavailable' };
			entry.listeners.forEach((listener) => this.notify(entry, listener));
		});
		this.entries.clear();
		this.updateRequests();
	};

	private notify(entry: Entry, listener: Listener): void {
		try {
			listener(entry.snapshot);
		} catch {
			entry.listeners.delete(listener);
			if (!entry.listeners.size && this.entries.get(keyFor(entry.request)) === entry) {
				this.entries.delete(keyFor(entry.request));
				this.updateRequests();
			}
		}
	}

	private updateRequests(): void {
		const requests = Array.from(this.entries.values(), (entry) => entry.request);
		if (
			requests.length === this.requests.length &&
			requests.every((request, i) => request === this.requests[i])
		)
			return;
		this.requests = requests;
		this.requestListeners.forEach((listener) => listener());
	}
}
